import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const AgentMaster = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [formData, setFormData] = useState({
    agent_code: '',
    agent_name: ''
  });

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { api } = useAuth();

  // Fetch Agent Master data
  const { data: agentData, isLoading, error, refetch } = useQuery(
    ['agentMaster', page, rowsPerPage, searchTerm],
    async () => {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await api.get(`/masters/agents?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        enqueueSnackbar(`Error fetching agent data: ${error.message}`, { variant: 'error' });
      }
    }
  );

  // Create mutation
  const createMutation = useMutation(
    (data) => api.post('/masters/agents', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('agentMaster');
        enqueueSnackbar('Agent created successfully', { variant: 'success' });
        handleCloseDialog();
      },
      onError: (error) => {
        enqueueSnackbar(`Error creating agent: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    ({ id, data }) => api.put(`/masters/agents/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('agentMaster');
        enqueueSnackbar('Agent updated successfully', { variant: 'success' });
        handleCloseDialog();
      },
      onError: (error) => {
        enqueueSnackbar(`Error updating agent: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    (id) => api.delete(`/masters/agents/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('agentMaster');
        enqueueSnackbar('Agent deleted successfully', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error deleting agent: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.agent_code, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      agent_code: item.agent_code,
      agent_name: item.agent_name
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({
      agent_code: '',
      agent_name: ''
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this agent?')) {
      deleteMutation.mutate(id);
    }
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading agent data: {error.message}
        </Alert>
      </Box>
    );
  }

  const agents = agentData?.data?.agents || agentData?.data || [];
  const totalCount = agentData?.data?.pagination?.total || agents.length;

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Agent Master
      </Typography>

      {/* Search and Actions */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by code or name..."
              InputProps={{
                endAdornment: <SearchIcon />
              }}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() => refetch()}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
            >
              Add Agent
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Agent Code</TableCell>
              <TableCell>Agent Name</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : agents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No agents found
                </TableCell>
              </TableRow>
            ) : (
              agents.map((agent) => (
                <TableRow key={agent.agent_code}>
                  <TableCell>{agent.agent_code}</TableCell>
                  <TableCell>{agent.agent_name}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(agent)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(agent.agent_code)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingItem ? 'Edit Agent' : 'Add New Agent'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Agent Code"
                  value={formData.agent_code}
                  onChange={(e) => setFormData({ ...formData, agent_code: e.target.value.toUpperCase() })}
                  required
                  disabled={!!editingItem}
                  inputProps={{ maxLength: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Agent Name"
                  value={formData.agent_name}
                  onChange={(e) => setFormData({ ...formData, agent_name: e.target.value })}
                  required
                  inputProps={{ maxLength: 30 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default AgentMaster;